import React, { useMemo, useState } from "react";
import { FiDownload, FiUpload, FiX } from "react-icons/fi";
import { api } from "../utils/api";
import { useToast } from "./ToastProvider";
import "../styles/Modal.css";

const ACCEPTED_COLUMNS = [
  "original_url",
  "title",
  "custom_code",
  "campaign_id",
  "campaign_name",
  "domain",
  "password",
  "tags",
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "expires_at",
  "scheduled_at",
  "redirect_type",
  "redirect_time",
  "mobile_url",
  "timezone",
];

function parseCsvLine(line) {
  const values = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
}

function parseCsv(csvText) {
  const lines = csvText
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { headers: [], rows: [] };
  }

  const headers = parseCsvLine(lines[0]).map((h) => h.trim().toLowerCase());
  const rows = lines.slice(1).map((line, idx) => {
    const parts = parseCsvLine(line);
    const row = {};
    headers.forEach((header, i) => {
      row[header] = parts[i] || "";
    });
    row.__rowNumber = idx + 2;
    return row;
  });

  return { headers, rows };
}

function isValidHttpUrl(url) {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export default function BulkLinkImportModal({ onClose, onImported, defaultCampaignId = "" }) {
  const { addToast } = useToast();
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);

  const validation = useMemo(() => {
    if (!rows.length) {
      return { validRows: [], errors: [] };
    }

    const errors = [];
    const validRows = [];

    if (!headers.includes("original_url")) {
      errors.push({ row: 1, error: "Missing required column: original_url" });
      return { validRows: [], errors };
    }

    rows.forEach((row) => {
      const originalUrl = String(row.original_url || "").trim();
      if (!originalUrl) {
        errors.push({ row: row.__rowNumber, error: "original_url is required" });
        return;
      }

      if (!isValidHttpUrl(originalUrl)) {
        errors.push({ row: row.__rowNumber, error: "original_url must be a valid HTTP/HTTPS URL" });
        return;
      }

      validRows.push(row);
    });

    return { validRows, errors };
  }, [headers, rows]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setParsing(true);
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (!parsed.rows.length) {
        addToast("CSV file has no data rows", "error");
        setHeaders([]);
        setRows([]);
      } else {
        setHeaders(parsed.headers);
        setRows(parsed.rows);
      }
    } catch (err) {
      console.error(err);
      addToast("Failed to parse CSV file", "error");
      setHeaders([]);
      setRows([]);
    } finally {
      setParsing(false);
      event.target.value = "";
    }
  };

  const downloadTemplate = () => {
    const template = [
      ACCEPTED_COLUMNS.join(","),
      "https://example.com/product,Spring Product,/spring-product,,,sqir.link,,marketing,newsletter,social,spring_launch,,hero_banner,,,302,10,,UTC",
    ].join("\n");

    const blob = new Blob([template], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "linksquire-import-template.csv";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const runImport = async () => {
    if (!validation.validRows.length) {
      addToast("No valid rows to import", "error");
      return;
    }

    setImporting(true);
    try {
      const result = await api.importLinks(validation.validRows, defaultCampaignId);
      const message = `Imported ${result.created_count} links${result.failed_count ? `, ${result.failed_count} failed` : ""}`;
      addToast(message, result.failed_count ? "warning" : "success");
      onImported?.();
      onClose();
    } catch (err) {
      console.error(err);
      addToast(err.message || "Import failed", "error");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Import Links (CSV)</h2>
          <button className="modal-close" onClick={onClose}>
            <FiX size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            <button type="button" className="btn-secondary" onClick={downloadTemplate}>
              <FiDownload /> Template CSV
            </button>
            <label className="btn-primary" style={{ cursor: "pointer" }}>
              <FiUpload /> Upload CSV
              <input type="file" accept=".csv,text/csv" onChange={handleFileChange} style={{ display: "none" }} />
            </label>
          </div>

          <p style={{ color: "var(--text-secondary)", fontSize: 13, marginBottom: 10 }}>
            Required: <code>original_url</code>. Optional columns include campaign fields, UTM fields, tags, scheduling, and redirect settings.
          </p>

          {parsing && <p style={{ fontSize: 13 }}>Parsing file...</p>}

          {!!headers.length && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginBottom: 6 }}>
                Detected columns
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {headers.map((h) => (
                  <span key={h} style={{ fontSize: 12, padding: "3px 8px", background: "var(--surface-hover)", border: "1px solid var(--border)", borderRadius: 999 }}>
                    {h}
                  </span>
                ))}
              </div>
            </div>
          )}

          {!!rows.length && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 8, overflow: "hidden" }}>
              <div style={{ padding: "10px 12px", background: "var(--surface-hover)", borderBottom: "1px solid var(--border)", fontSize: 13 }}>
                Preview ({validation.validRows.length} valid / {rows.length} rows)
              </div>
              <div style={{ maxHeight: 180, overflow: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid var(--border)" }}>Row</th>
                      <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid var(--border)" }}>original_url</th>
                      <th style={{ textAlign: "left", padding: 8, borderBottom: "1px solid var(--border)" }}>title</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 12).map((row) => (
                      <tr key={row.__rowNumber}>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>{row.__rowNumber}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>{row.original_url || "-"}</td>
                        <td style={{ padding: 8, borderBottom: "1px solid var(--border)" }}>{row.title || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!!validation.errors.length && (
            <div style={{ marginTop: 12, border: "1px solid #f5c2c7", background: "#fff1f2", color: "#8a1c1f", borderRadius: 8, padding: 10 }}>
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Validation issues</div>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12 }}>
                {validation.errors.slice(0, 8).map((e, idx) => (
                  <li key={`${e.row}-${idx}`}>Row {e.row}: {e.error}</li>
                ))}
              </ul>
              {validation.errors.length > 8 && (
                <div style={{ fontSize: 12, marginTop: 6 }}>+{validation.errors.length - 8} more errors</div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={importing}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={runImport}
            disabled={importing || parsing || !validation.validRows.length}
          >
            {importing ? "Importing..." : "Import Links"}
          </button>
        </div>
      </div>
    </div>
  );
}
